import { action, computed, observable } from 'mobx';
import { PagedList } from '../../../../admin/types/pagination';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { UrlBoundParam } from '../../../../commonv3/states/UrlBoundParam';
import { CourseWorkSubmissionApis, SubmissionQueryParams } from '../../../apis/courseWorkSubmissions';
import { UserId } from '../../../../mainv2/types/user';
import { LessonHomeworkSubmissionApis } from '../../../apis/lessonHomeworkSubmissions';
import { translateErrorMessage } from '../../../../commonv3/api/protocol';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { ReviewableCoursesApis } from '../../../apis/reviewableCourses';
import { CourseId } from '../../../types/courses';
import { CourseWorkSubmissionIndexView, CourseWorkSubmissionTargetType } from '../../../types/course_work_submissions';
import { ExamPracticalSubmissionApis } from '../../../apis/examPracticalSectionSubmissions';

export enum TabType {
    MINE_UNCOMPLETED = 'mine_uncompleted',
    MINE_COMPLETED = 'mine_completed',
    ALL = 'all',
}

export const DEADLINE_FILTER_ALL = -1;

export class CourseWorkSubmissionListState {
    @observable mainTab = UrlBoundParam.stringParam('main_tab', TabType.MINE_UNCOMPLETED);
    @observable selectedCourseIds = UrlBoundParam.numericArrayParam('course_ids', []);
    @observable maxDaysBeforeDeadline = UrlBoundParam.integerParam('max_days_before_deadline', DEADLINE_FILTER_ALL);
    @observable reviewableCourses = RemoteResourceV3.loading();
    @observable submissions: RemoteResourceV3<PagedList<CourseWorkSubmissionIndexView>> = RemoteResourceV3.loading();
    @observable page = UrlBoundParam.integerParam('page', 1);

    @action.bound
    async init() {
        const courses = await this.loadCourses();
        if (courses && this.selectedCourseIds.get().length === 0) {
            this.selectedCourseIds.set(courses.map((c) => c.id));
        }
        await this.loadSubmissions();
    }

    @action.bound
    async loadCourses() {
        return await updateRemoteResourceV3(
            this.reviewableCourses,
            (v) => (this.reviewableCourses = v),
            () => ReviewableCoursesApis.findAll()
        );
    }

    @action.bound
    async loadSubmissions() {
        await updateRemoteResourceV3(
            this.submissions,
            (v) => (this.submissions = v),
            () => CourseWorkSubmissionApis.findAll(this.page.get(), this.queryParams)
        );
    }

    get queryParamForCourseIds() {
        // コースIDが選択されていない場合には課題を表示したくない。
        // GETパラメータ上で空配列と未指定をうまく区別できないので、無効なコースIDを指定する
        const EMPTY_COURSE_ID = '-1';
        return this.selectedCourseIds.get().length > 0 ? this.selectedCourseIds.get().join(',') : EMPTY_COURSE_ID;
    }

    get deadlineFilterSpecified() {
        return this.maxDaysBeforeDeadline.get() >= 0;
    }

    @computed
    get queryParams(): Partial<SubmissionQueryParams> {
        const params = {
            course_ids: this.queryParamForCourseIds,
        };
        switch (this.mainTab.get()) {
            case TabType.MINE_UNCOMPLETED:
                params['reviewer_id'] = GlobalStatesV3.currentUser.currentUser.id;
                params['review_status'] = 'uncompleted';
                break;
            case TabType.MINE_COMPLETED:
                params['reviewer_id'] = GlobalStatesV3.currentUser.currentUser.id;
                params['review_status'] = 'completed';
                break;
        }
        if (this.deadlineFilterSpecified) {
            params['max_days_before_deadline'] = this.maxDaysBeforeDeadline.get();
        }
        return params;
    }

    @action.bound
    async setMainTab(tab) {
        this.mainTab.set(tab);
        this.page.set(1);
        await this.loadSubmissions();
    }

    @action.bound
    async setSelectedCourseIds(courseIds: CourseId[]) {
        this.selectedCourseIds.set(courseIds);
        await this.loadSubmissions();
    }

    @action.bound
    async setMaxDaysBeforeDeadline(value) {
        this.maxDaysBeforeDeadline.set(value);
        await this.loadSubmissions();
    }

    @action.bound
    async setPage(page) {
        this.page.set(page);
        await this.loadSubmissions();
    }

    @action.bound
    async assignReviewer(submission: CourseWorkSubmissionIndexView, reviewerId: UserId) {
        try {
            switch (submission.course_work_submission_type) {
                case CourseWorkSubmissionTargetType.LESSON_HOMEWORK_SUBMISSION:
                    await LessonHomeworkSubmissionApis.assignReviewer(submission.token, reviewerId);
                    break;
                case CourseWorkSubmissionTargetType.EXAM_PRACTICAL_SECTION_SUBMISSION:
                    await ExamPracticalSubmissionApis.assignReviewer(submission.token, reviewerId);
                    break;
            }
            await this.loadSubmissions();
        } catch (e) {
            alert('担当者の割り当てに失敗しました: ' + translateErrorMessage(e));
        }
    }
}
